import { useMemo } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// uuid is a library for generating unique id
import { v4 as uuidv4 } from "uuid";

// @mui material components
import { Icon, IconButton, Table as MuiTable, Pagination } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";

// React components
import SoftBox from "components/SoftBox";
import SoftAvatar from "components/SoftAvatar";
import SoftTypography from "components/SoftTypography";

// React base styles
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";
import borders from "assets/theme/base/borders";
import { usePopover } from "hooks/use-popover";
import { useSearchParams } from "react-router-dom";

function Table({
  columns,
  rows,
  handleOpenFilterMenu,
  currentPage,
  pagination,
  setPage,
  pageCount,
  setQuery,
  limit,
  minHeight,
}) {
  const { light } = colors;
  const { size, fontWeightBold } = typography;
  const { borderWidth } = borders;
  const filterPopover = usePopover();
  const [searchParams, setSearchParams] = useSearchParams();

  const renderColumns = columns.map(({ name, align, width }, key) => {
    let pl;
    let pr;

    if (key === 0) {
      pl = 3;
      pr = 3;
    } else if (key === columns.length - 1) {
      pl = 3;
      pr = 3;
    } else {
      pl = 1;
      pr = 1;
    }

    return (
      <SoftBox
        key={name}
        component="th"
        width={width || "auto"}
        pt={1.5}
        pb={1.25}
        pl={align === "left" ? pl : 3}
        pr={align === "right" ? pr : 3}
        textAlign={align}
        fontSize={size.xxs}
        fontWeight={fontWeightBold}
        color="inherit"
        opacity={0.7}
        borderBottom={`${borderWidth[1]} solid ${light.main}`}
      >
        {name.toUpperCase()}
      </SoftBox>
    );
  });

  const renderRows = rows.map((row, key) => {
    const rowKey = `row-${key}`;

    const tableRow = columns.map(({ name, align }) => {
      let template;

      if (Array.isArray(row[name])) {
        template = (
          <SoftBox
            key={uuidv4()}
            component="td"
            p={1}
            borderBottom={row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null}
          >
            <SoftBox display="flex" alignItems="center" py={0.5} px={1}>
              <SoftBox mr={2}>
                <SoftAvatar src={row[name][0]} name={row[name][1]} variant="rounded" size="sm" />
              </SoftBox>
              <SoftTypography variant="button" fontWeight="medium" sx={{ width: "max-content" }}>
                {row[name][1]}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        );
      } else {
        template = (
          <SoftBox
            key={uuidv4()}
            component="td"
            p={1}
            textAlign={align}
            borderBottom={row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null}
          >
            <SoftTypography
              variant="button"
              fontWeight="regular"
              color="inherit"
              sx={{ display: "inline-block", width: "max-content" }}
            >
              {row[name]}
            </SoftTypography>
          </SoftBox>
        );
      }

      return template;
    });

    return <TableRow key={rowKey}>{tableRow}</TableRow>;
  });
  return useMemo(
    () => (
      <TableContainer sx={{ paddingBottom: 3, height: minHeight, position: "relative" }}>
        <MuiTable>
          <SoftBox component="thead">
            <TableRow>{renderColumns}</TableRow>
          </SoftBox>
          <TableBody>{renderRows}</TableBody>
        </MuiTable>
        {pagination && limit !== "ALL" && (
          <SoftBox
            display="flex"
            justifyContent="flex-end"
            pt={2.5}
            pb={1}
            sx={{ position: "absolute", bottom: 20, right: 30 }}
          >
            <Pagination
              color="warning"
              count={pageCount || 0}
              sx={{
                "& .MuiPaginationItem-root.Mui-selected": {
                  color: "#fff",
                  background: "linear-gradient(135deg, #FFA757 0%, #D57013 100%)",
                  fontWeight: "500",
                },
                "& .MuiPaginationItem-root": {
                  color: "rgba(0, 0, 0, 0.65)",
                  background: "rgba(0, 0, 0, 0.05)",
                  fontWeight: "500",
                  margin: "0 4px",
                  fontSize: "12px",
                },
                "& .MuiPagination-ul li:first-of-type button, & .MuiPagination-ul li:last-child button":
                  {
                    background: "transparent",
                    fontSize: "20px",
                  },
              }}
              onChange={(_, page) => {
                setPage(page);
                setSearchParams({ ...Object.fromEntries(searchParams.entries()), page: page });
                setQuery((prev) => ({
                  ...prev,
                  queryMeta: {
                    paginate: true,
                    limit,
                    page,
                  },
                }));
              }}
              page={currentPage}
            />
          </SoftBox>
        )}
      </TableContainer>
    ),
    [columns, rows]
  );
}

// Setting default values for the props of Table
Table.defaultProps = {
  columns: [],
  rows: [{}],
  filter: false,
  setQuery: () => {},
  setPage: () => {},
  handleOpenFilterMenu: () => {},
};

// Typechecking props for the Table
Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
  pagination: PropTypes.bool,
  setQuery: PropTypes.any,
  minHeight: PropTypes.number,
  pageCount: PropTypes.number,
  setPage: PropTypes.any,
  limit: PropTypes.number,
  currentPage: PropTypes.number,
  handleOpenFilterMenu: PropTypes.any,
};

export default Table;
